/**
 * Picking one candidate per field, by a policy the spec states, and the
 * resolved fields as a reading in the shape `normalizeExtraction` takes.
 */

import { checkOrgNumber } from '../orgnumber.js';
import { parseAmount } from '../parse.js';
import { FIELD_KEYS, type Candidate, type FieldKey, type ReadingState } from './types.js';

/**
 * How a field is chosen:
 *
 * - `prefer`: the first source in the list that answered for the field, even
 *   with `null` - a model that says "not printed" is taken at its word, and
 *   a reading that wins keeps its gaps, as production's does.
 * - `fill`: the first source in the list that found a value.
 * - `vote`: the value most sources agree on, weighted by confidence; ties go
 *   to the source listed first.
 * - `checksum`: `fill`, among the organisation numbers that pass their
 *   checksum.
 *
 * A source not listed is never chosen, though its candidates stay on record.
 */
export type FieldPolicy =
  | { prefer: string[] }
  | { fill: string[] }
  | { vote: string[] }
  | { checksum: string[] };

export type ResolvePolicy = Partial<Record<FieldKey, FieldPolicy>> & {
  /** For every field the policy does not name. */
  default?: FieldPolicy;
};

/** A value worth choosing: not missing, not blank. An empty list counts - "no VAT table" is an answer. */
export function hasValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function sourcesOf(policy: FieldPolicy): string[] {
  if ('prefer' in policy) return policy.prefer;
  if ('fill' in policy) return policy.fill;
  return 'vote' in policy ? policy.vote : policy.checksum;
}

/** Candidates in the policy's source order, the listed ones only. */
function inOrder(candidates: readonly Candidate[], sources: readonly string[], valued: boolean): Candidate[] {
  const out: Candidate[] = [];
  for (const source of sources) {
    out.push(...candidates.filter((candidate) => candidate.source === source && (!valued || hasValue(candidate.value))));
  }
  return out;
}

/** What makes two values the same answer: an amount by its öre, anything else by its JSON. */
function voteKey(value: unknown): string {
  const amount = typeof value === 'number' || typeof value === 'string' ? parseAmount(value) : null;
  return amount !== null ? `amount:${Math.round(amount * 100)}` : `json:${JSON.stringify(value)}`;
}

export function chooseCandidate(candidates: readonly Candidate[], policy: FieldPolicy): Candidate | null {
  const ordered = inOrder(candidates, sourcesOf(policy), !('prefer' in policy));
  if ('checksum' in policy) {
    return ordered.find((candidate) => typeof candidate.value === 'string' && checkOrgNumber(candidate.value).valid) ?? null;
  }
  if ('vote' in policy) {
    const tally = new Map<string, { weight: number; first: Candidate }>();
    for (const candidate of ordered) {
      const key = voteKey(candidate.value);
      const entry = tally.get(key) ?? { weight: 0, first: candidate };
      entry.weight += candidate.confidence ?? 1;
      tally.set(key, entry);
    }
    let best: { weight: number; first: Candidate } | null = null;
    // Map order is first-seen order, which is the policy's source order: a tie keeps the earlier source.
    for (const entry of tally.values()) if (!best || entry.weight > best.weight) best = entry;
    return best?.first ?? null;
  }
  return ordered[0] ?? null;
}

/**
 * Every field chosen by `policy`. Item categories are only chosen from a
 * candidate that files the item list chosen, in its order: categories from
 * one reading do not fit another reading's lines.
 */
export function resolveFields(state: Readonly<ReadingState>, policy: ResolvePolicy): Partial<Record<FieldKey, Candidate>> {
  const resolved: Partial<Record<FieldKey, Candidate>> = {};
  for (const field of FIELD_KEYS) {
    if (field === 'itemCategories') continue;
    const fieldPolicy = policy[field] ?? policy.default;
    if (!fieldPolicy) continue;
    const chosen = chooseCandidate(state.candidates[field] ?? [], fieldPolicy);
    if (chosen) resolved[field] = chosen;
  }
  const itemsSource = resolved.items?.source;
  const categoryPolicy = policy.itemCategories ?? policy.default;
  if (itemsSource && categoryPolicy) {
    const fitting = (state.candidates.itemCategories ?? []).filter((candidate) => candidate.forItems === itemsSource);
    const chosen = chooseCandidate(fitting, categoryPolicy);
    if (chosen) resolved.itemCategories = chosen;
  }
  return resolved;
}

const HEADER_FIELDS = [
  'purchasedAt', 'currency', 'total', 'subtotal', 'discountTotal', 'roundingAmount', 'depositTotal', 'vatLines',
  'paymentMethod', 'cardLast4', 'receiptNumber', 'terminalId', 'cashier', 'confidence',
] as const satisfies readonly FieldKey[];

/**
 * The resolved fields as a reading, in the shape a model answers in, for
 * `normalizeExtraction`: the merchant with the chosen organisation number,
 * each item with its chosen category, and `null` for what nothing found.
 */
export function readingFrom(resolved: Readonly<Partial<Record<FieldKey, Candidate>>>): Record<string, unknown> {
  const value = (field: FieldKey): unknown => resolved[field]?.value ?? null;
  const merchantValue = value('merchant');
  const merchant: Record<string, unknown> =
    merchantValue && typeof merchantValue === 'object' && !Array.isArray(merchantValue) ? { ...(merchantValue as Record<string, unknown>) } : {};
  if (resolved.orgNumber) merchant.orgNumber = resolved.orgNumber.value;

  const reading: Record<string, unknown> = { merchant };
  for (const field of HEADER_FIELDS) reading[field] = value(field);

  const items = Array.isArray(value('items')) ? (value('items') as unknown[]) : [];
  const categories = Array.isArray(value('itemCategories')) ? (value('itemCategories') as unknown[]) : null;
  reading.items = items.map((item, index) => {
    const row = item && typeof item === 'object' ? { ...(item as Record<string, unknown>) } : {};
    // A filing is the category key, or that with its confidence and alternatives (`lineFiling`).
    const filing = categories ? (categories[index] ?? null) : undefined;
    if (filing && typeof filing === 'object') Object.assign(row, filing);
    else if (categories) row.category = filing;
    return row;
  });
  reading.category = value('receiptCategory');
  reading.otherCategories = Array.isArray(value('otherCategories')) ? value('otherCategories') : [];
  return reading;
}
